/**
 * Forge logo — the official brand mark.
 *
 * Mark: a 3×3 checker grid where the corner + center cells are
 * filled with the brand emerald, and the edge cells are filled with
 * zinc-700. The pattern reads as a stylized "F" stencil and as an
 * operator's-grid simultaneously, which lines up with the
 * "operations terminal" aesthetic the rest of the brand inherits.
 *
 *     ■ □ ■
 *     □ ■ □
 *     ■ □ ■
 *
 * Three render modes:
 *   - mark        → just the grid (used as favicon, in tight UI like
 *                   the auth-layout corner badge, sidebar collapsed)
 *   - wordmark    → grid + "Forge" in Fraunces serif (default for nav,
 *                   headers, footers)
 *   - full        → wordmark + " / OS" mono tagline (only used when
 *                   the page has room — Landing/Demo headers)
 *
 * Size scales the mark proportionally and bumps the wordmark:
 *   xs (16px mark) — favicon-equivalent inline
 *   sm (20px)      — sidebar, condensed header
 *   md (28px)      — auth pages, marketing nav (default)
 *   lg (36px)      — Landing hero header
 *   xl (56px)      — splash / login / 404 hero
 *
 * The wordmark requires the Fraunces font face. If the consuming
 * page doesn't @import the font, the wordmark falls back to ui-serif
 * which still reads acceptably (we don't want the page to break
 * just because the network is slow).
 */

import { Link } from 'react-router-dom'

export type LogoSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
export type LogoVariant = 'mark' | 'wordmark' | 'full'

const MARK_PX: Record<LogoSize, number> = {
  xs: 14, sm: 18, md: 24, lg: 32, xl: 48,
}
const WORDMARK_TEXT_CLASS: Record<LogoSize, string> = {
  xs: 'text-sm',
  sm: 'text-base',
  md: 'text-lg',
  lg: 'text-2xl',
  xl: 'text-3xl',
}
const TAGLINE_TEXT_CLASS: Record<LogoSize, string> = {
  xs: 'text-[8px]',
  sm: 'text-[9px]',
  md: 'text-[10px]',
  lg: 'text-[10px]',
  xl: 'text-xs',
}
const GAP_PX: Record<LogoSize, number> = {
  xs: 1, sm: 2, md: 2, lg: 3, xl: 4,
}

/**
 * Just the mark — no wordmark. Used in tight UI and as the visual
 * backbone of the favicon. Renders 9 absolute-positioned spans inside
 * a square grid so the proportion is stable across browsers.
 */
function Mark({ size = 'md', className = '' }: { size?: LogoSize; className?: string }) {
  const px = MARK_PX[size]
  const gap = GAP_PX[size]
  // Even indices (0, 2, 4, 6, 8 — corners + center) are brand;
  // odd indices (1, 3, 5, 7 — edge midpoints) are zinc-700.
  const cellSize = (px - gap * 2) / 3
  return (
    <div
      className={`grid grid-cols-3 shrink-0 ${className}`}
      style={{ width: px, height: px, gap }}
      aria-hidden="true"
    >
      {Array.from({ length: 9 }).map((_, i) => (
        <span
          key={i}
          className={i % 2 === 0 ? 'bg-brand-500' : 'bg-zinc-700'}
          style={{ width: cellSize, height: cellSize }}
        />
      ))}
    </div>
  )
}

export function Logo({
  variant = 'wordmark',
  size = 'md',
  className = '',
  href,
  tone = 'auto',
}: {
  variant?: LogoVariant
  size?: LogoSize
  className?: string
  /**
   * If supplied, the logo is wrapped in a Link to this route. Useful
   * in headers where the logo should navigate home.
   */
  href?: string
  /**
   * `auto` uses the surrounding text color (zinc-100 on dark / zinc-900
   * on light themes); `light` and `dark` force one. Tagline always
   * uses the muted zinc-500 since it's a label, not body copy.
   */
  tone?: 'auto' | 'light' | 'dark'
}) {
  const wordmarkColor =
    tone === 'light' ? 'text-zinc-100'
    : tone === 'dark' ? 'text-zinc-900'
    : 'text-zinc-100'

  const inner = (
    <div className={`inline-flex items-center gap-2.5 ${className}`}>
      <Mark size={size} />
      {variant !== 'mark' && (
        <span
          className={`tracking-tight ${WORDMARK_TEXT_CLASS[size]} ${wordmarkColor}`}
          style={{ fontFamily: "'Fraunces', ui-serif, Georgia, serif", letterSpacing: '-0.02em' }}
        >
          Forge
        </span>
      )}
      {variant === 'full' && (
        <span
          className={`uppercase tracking-[0.2em] text-zinc-500 ml-1 ${TAGLINE_TEXT_CLASS[size]}`}
          style={{ fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace" }}
        >
          / OS
        </span>
      )}
    </div>
  )

  if (href) {
    return (
      <Link
        to={href}
        className="inline-flex items-center gap-2.5 hover:opacity-90 transition-opacity"
        aria-label="Forge — home"
      >
        {inner}
      </Link>
    )
  }
  return inner
}

export default Logo
